// The organisation's run history (docs/spec/transport.md slice 9): every run of the last days, and
// each one's full record: the way driven on the map over the planned way, each stop's planned and
// actual time, and every child on and off in order.
import { useState } from "react";
import { Badge, Button, Select, Table } from "@vavr/ui";
import { time } from "@vavr/screens/shared";
import { LoadError, Loading, useGet } from "@vavr/screens/console";
import { stopStates, useRunRecord, type RunRecord, type Shape } from "./fleet.tsx";
import { lngLat, RunMap } from "./runmap.tsx";

interface RunRow { id: string; date: string; route: string; kind: string; vehicle: string | null; driver: string | null; started_at: number; finished_at: number | null; distance_mi: number; points: number }

const EVENT: Record<string, string> = { board: "Got on", alight: "Got off", absent: "Not at the stop", held: "Kept on the bus" };
const minutes = (ms: number) => { const m = Math.round(ms / 60_000); return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`; };

export function Runs({ base, tz }: { base: string; tz: string }) {
  const [days, setDays] = useState("14");
  const list = useGet<{ runs: RunRow[] }>(`${base}/runs?days=${days}`);
  const [open, setOpen] = useState<string | null>(null);
  if (open) return <RunRecordView base={base} runId={open} tz={tz} onBack={() => setOpen(null)} />;
  if (list.loading) return <Loading />;
  if (list.error) return <LoadError error={list.error} retry={list.reload} />;
  return (
    <div className="pmu-stack">
      <div className="pmu-row pmu-row--between">
        <p className="pmu-muted">Every run is kept: the way the bus went, each stop's time, and every child on and off.</p>
        <Select label="Show" value={days} onValueChange={(v) => setDays(v ?? "14")} options={[{ value: "7", label: "Last 7 days" }, { value: "14", label: "Last 14 days" }, { value: "30", label: "Last 30 days" }, { value: "90", label: "Last 90 days" }]} />
      </div>
      <Table
        caption="Runs" hideCaption rowKey={(r) => r.id} rows={list.data?.runs ?? []} empty="No runs in this period."
        columns={[
          { key: "date", header: "Date", render: (r) => r.date },
          { key: "route", header: "Route", rowHeader: true, render: (r) => <button type="button" className="pmu-c-linkbutton" onClick={() => setOpen(r.id)}>{r.route}</button> },
          { key: "driver", header: "Driver", render: (r) => r.driver ?? "—" },
          { key: "vehicle", header: "Vehicle", render: (r) => r.vehicle ?? "—" },
          { key: "time", header: "Time", render: (r) => `${time(r.started_at, tz)}${r.finished_at ? ` – ${time(r.finished_at, tz)}` : ""}` },
          { key: "length", header: "Length", align: "end", render: (r) => (r.finished_at ? minutes(r.finished_at - r.started_at) : <Badge tone="info">On the road</Badge>) },
          { key: "distance", header: "Distance", align: "end", render: (r) => `${r.distance_mi} mi` },
        ]}
      />
    </div>
  );
}

export function RunRecordView({ base, runId, tz, onBack }: { base: string; runId: string; tz: string; onBack: () => void }) {
  const probe = useGet<RunRecord>(`${base}/runs/${runId}`);
  const rec = useRunRecord(base, runId, !!probe.data && !probe.data.run.finished_at) ?? probe.data;
  const shapes = useGet<{ shapes: Shape[] }>(`${base}/shapes`);
  if (probe.loading) return <Loading />;
  if (probe.error || !rec) return <LoadError error={probe.error ?? new Error("The run did not load.")} retry={probe.reload} />;
  const r = rec.run;
  const stops = stopStates(rec);
  const last = rec.trail.at(-1);
  const plan = shapes.data?.shapes.find((s) => s.route_id === r.route_id)?.shape ?? null;
  return (
    <div className="pmu-stack">
      <div className="pmu-row pmu-row--between">
        <Button variant="ghost" icon="chevron-left" onClick={onBack}>All runs</Button>
        <Badge tone={r.finished_at ? "success" : "info"}>{r.finished_at ? `Finished ${time(r.finished_at, tz)}` : "On the road"}</Badge>
      </div>
      <header>
        <h2 className="pmt-card__title">{r.route} · {r.date}</h2>
        <p className="pmu-small pmu-muted">{r.kind === "am" ? "To school" : r.kind === "pm" ? "From school" : "Trip"} · {r.vehicle ?? "No vehicle"} · {r.driver ?? "No driver"}</p>
      </header>
      <dl className="pmt-fleetsum">
        <div><dt>Started</dt><dd>{time(r.started_at, tz)}</dd></div>
        <div><dt>Length</dt><dd>{minutes((r.finished_at ?? Date.now()) - r.started_at)}</dd></div>
        <div><dt>Driven</dt><dd>{r.distance_mi} mi</dd></div>
        <div><dt>Got on</dt><dd>{r.boarded}</dd></div>
        <div><dt>Got off</dt><dd>{r.dropped}</dd></div>
        <div data-tone={r.absent ? "warning" : undefined}><dt>Not at the stop</dt><dd>{r.absent}</dd></div>
        <div data-tone={r.held ? "danger" : undefined}><dt>Kept on the bus</dt><dd>{r.held}</dd></div>
        <div><dt>Positions kept</dt><dd>{rec.trail.length}</dd></div>
      </dl>
      <RunMap id={r.id} plan={plan} trail={lngLat(rec.trail)} stops={stops} height={460} label={`${r.route} on ${r.date}`}
        bus={!r.finished_at && last ? { lat: last.lat, lng: last.lng, heading: last.heading, title: "The bus" } : null} />
      <p className="pmu-small pmu-muted"><span className="pmt-key pmt-key--driven" /> The way the bus went <span className="pmt-key pmt-key--planned" /> The planned way</p>
      <div className="pmt-record">
        <section aria-labelledby="pmt-rec-stops">
          <h3 id="pmt-rec-stops" className="pmt-card__title">Stops</h3>
          <Table caption="Stops" hideCaption rowKey={(x) => x.id} rows={[...rec.stops].sort((a, b) => a.seq - b.seq)} empty="No stops."
            columns={[
              { key: "n", header: "#", render: (x) => String(stops.findIndex((s) => s.key === x.id) + 1) },
              { key: "name", header: "Stop", rowHeader: true, render: (x) => x.name },
              { key: "planned", header: "Planned", render: (x) => (x.planned_at ? time(x.planned_at, tz) : "—") },
              { key: "actual", header: "Actual", render: (x) => (x.arrived_at ? time(x.arrived_at, tz) : "—") },
              { key: "late", header: "", render: (x) => (x.late_min == null ? null : x.late_min >= 5 ? <Badge tone="warning">{x.late_min} min late</Badge> : x.late_min <= -5 ? <Badge tone="info">{-x.late_min} min early</Badge> : <Badge tone="success">On time</Badge>) },
            ]} />
        </section>
        <section aria-labelledby="pmt-rec-timeline">
          <h3 id="pmt-rec-timeline" className="pmt-card__title">Every child on and off</h3>
          {rec.timeline.length === 0 ? <p className="pmu-muted">Nobody was recorded on this run.</p> : (
            <ol className="pmt-timeline">
              {rec.timeline.map((x, i) => (
                <li key={i} data-type={x.type}>
                  <span className="pmt-timeline__time">{time(x.at, tz)}</span>
                  <span><b>{x.child}</b> · {EVENT[x.type] ?? x.type}{x.stop ? ` at ${x.stop}` : ""}{x.adult ? `, to ${x.adult}` : ""}
                    {x.flags.includes("unauthorised_adult") && <Badge tone="danger">Not an approved adult</Badge>}{x.flags.includes("restriction_hit") && <Badge tone="danger">Restriction</Badge>}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}
